import { c as _c } from "react/compiler-runtime";
import '@onefootprint/footprint-js/dist/footprint-js.css';
import { useShortcut } from '@onefootprint/ui';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import Layout from '@/components/layout';
import Providers from '@/components/providers';
import { RSG_SHORTCUT_KEYS } from '@/config/constants';
import '@/styles/globals.css';
import '@/components/agent-chat/components/chat-input/chat-input.css';
import '@/features/agent-builder/styles/percy-prose.css';
import '@/features/agent-builder/styles/prompt-reference.css';
import '@/features/investigation/styles/artifact-blocks.css';
import '@/pages/authentication/styles/auth-brand.css';
import '@onefootprint/ui/notion-editor.css';
import { useQueryState } from 'nuqs';
import { NuqsAdapter } from 'nuqs/adapters/next/pages';
import RiskSignalsGlossary from '@/components/risk-signals-glossary';
import { NonceProvider } from '@/hooks/use-nonce';
const layouts = {
  '/playbooks/[id]': 'full',
  '/playbooks/new': 'full',
  '/agents/builder': 'full',
  '/agents/[id]/builder': 'full',
  '/agents/[id]': 'blank',
  '/users/[id]/onboardings/[onboarding_id]/trace': 'full',
  '/businesses/[id]/onboardings/[onboarding_id]/trace': 'full',
  '/internal/graph/[id]': 'blank',
  '/ask-percy': 'full',
  '/chat/[workflowId]': 'full',
  '/investigations/[investigationId]': 'full',
  '/developers/appearance/[id]': 'full',
  '/appearance-preview': 'blank'
};
const VIEWPORT = 'width=device-width, initial-scale=1, viewport-fit=cover, interactive-widget=resizes-content';

// useQueryState must be called from below NuqsAdapter, so this can't live in App itself
const RiskSignalsGlossaryContainer = () => {
  const $ = _c(10);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      history: "replace"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [rsg, setRsg] = useQueryState("rsg", t0);
  let t1;
  if ($[1] !== setRsg) {
    t1 = () => {
      setRsg("open");
    };
    $[1] = setRsg;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleRsgOpen = t1;
  let t2;
  if ($[3] !== handleRsgOpen) {
    t2 = {
      keys: RSG_SHORTCUT_KEYS,
      onShortcut: handleRsgOpen
    };
    $[3] = handleRsgOpen;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useShortcut(t2);
  const t3 = rsg === "open";
  let t4;
  if ($[5] !== setRsg) {
    t4 = () => {
      setRsg(null);
    };
    $[5] = setRsg;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <RiskSignalsGlossary open={t3} onClose={t4} />;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};
const App = t0 => {
  const $ = _c(9);
  const {
    Component,
    pageProps
  } = t0;
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const layout = pageProps.layout || layouts[router.pathname as keyof typeof layouts] || "default";
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setMounted(true);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  if (!mounted) {
    return null;
  }
  const isIos = CSS.supports("-webkit-touch-callout", "none");
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Head><meta name="viewport" content={isIos ? `${VIEWPORT}, maximum-scale=1` : VIEWPORT} /></Head>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== Component) {
    t4 = <Component />;
    $[3] = Component;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <RiskSignalsGlossaryContainer />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== layout || $[7] !== t4) {
    t6 = <NonceProvider>{t3}<NuqsAdapter><Providers><Layout name={layout}>{t4}{t5}</Layout></Providers></NuqsAdapter></NonceProvider>;
    $[6] = layout;
    $[7] = t4;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
};
export default App;